import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlotlyModule } from 'angular-plotly.js';
import { ProductionBatch, Scan, InspectionModel } from '../../../model';
import { AxisSpans } from './batch-analysis.component';

@Component({
  selector: 'app-batch-measured-3d-plot',
  standalone: true,
  imports: [CommonModule, PlotlyModule],
  template: `
    <div class="plot-container">
      <plotly-plot
        [data]="plotData()"
        [layout]="plotLayout()"
        [config]="plotConfig()"
        (plotlyClick)="onPointClick($event)"
        [style]="{ width: '100%', height: '100%' }"
      ></plotly-plot>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        isolation: isolate;
        border: 1px solid #d9d9d9;
      }
      .plot-container {
        width: 100%;
        height: 600px;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchMeasured3dPlotComponent {
  batch = input.required<ProductionBatch>();
  model = input<InspectionModel | null>(null);
  axisSpans = input<AxisSpans | null>(null);
  scanSelected = output<Scan>();

  plotData = computed(() => {
    const b = this.batch();
    if (!b?.scans) return [];

    const scans = b.scans.filter((s) => !!s.median);

    const x = scans.map((s) => s.median!.l);
    const y = scans.map((s) => s.median!.a);
    const z = scans.map((s) => s.median!.b);

    const text = scans.map((s) => {
      const lab = s.median!;
      return `Scan: ${s.sequenceInBatch}<br>Status: ${s.state}<br>L*: ${lab.l.toFixed(2)}<br>a*: ${lab.a.toFixed(2)}<br>b*: ${lab.b.toFixed(2)}`;
    });

    const getColor = (s: Scan) => {
      switch (s.state) {
        case 'Passed':
          return '#28a745';
        case 'Failed':
          return '#dc3545';
        case 'Ignored':
          return '#6c757d';
        case 'Scanning':
          return '#007bff';
        default:
          return '#ffc107';
      }
    };

    return [
      {
        x,
        y,
        z,
        mode: 'markers',
        type: 'scatter3d',
        text,
        hoverinfo: 'text',
        customdata: scans.map((s) => s.id),
        marker: {
          size: 5,
          color: scans.map((s) => getColor(s)),
          opacity: 0.8,
          line: { color: 'white', width: 0.5 },
        },
      },
    ];
  });

  plotLayout = computed(() => {
    const data = this.plotData();
    const spans = this.axisSpans();
    const allX: number[] = [];
    const allY: number[] = [];
    const allZ: number[] = [];
    for (const trace of data) {
      if (Array.isArray(trace.x)) allX.push(...trace.x);
      if (Array.isArray(trace.y)) allY.push(...trace.y);
      if (Array.isArray(trace.z)) allZ.push(...trace.z);
    }

    const centeredRange = (values: number[], sharedSpan: number | undefined) => {
      if (!values.length) return undefined;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const center = (min + max) / 2;
      const span = sharedSpan ?? (max - min || 1);
      const half = span / 2;
      const pad = span * 0.1;
      return [center - half - pad, center + half + pad];
    };

    return {
      margin: { l: 0, r: 0, b: 0, t: 0 },
      scene: {
        xaxis: { title: { text: 'L*' }, range: centeredRange(allX, spans?.l) },
        yaxis: { title: { text: 'a*' }, range: centeredRange(allY, spans?.a) },
        zaxis: { title: { text: 'b*' }, range: centeredRange(allZ, spans?.b) },
        aspectmode: 'data',
      },
      showlegend: false,
    };
  });

  plotConfig = computed(() => ({
    responsive: true,
    displayModeBar: true,
    scrollZoom: true,
  }));

  onPointClick(event: unknown) {
    const point = (event as { points?: { customdata?: string }[] }).points?.[0];
    if (point?.customdata) {
      const scan = this.batch().scans.find((s) => s.id === point.customdata);
      if (scan) {
        this.scanSelected.emit(scan);
      }
    }
  }
}
